import React from 'react';
import type { Row } from '@tanstack/react-table';
import { ChevronDown, ChevronRight, Trash2, Undo2, X } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { ForeignKeyPopover } from './ForeignKeyPopover';
import { ArrayFkCell } from '../../common/components/ArrayFkCell';
import { EditableCell } from './EditableCell';
import { pinnedColumnBorderStyle } from '../../common/hooks/useColumnPinning';
import { resolveCellFill, MEANING_OUTLINE_CLASS } from '../../common/utils/selectionAffordance';
import { isExplicitlyNotJsonColumn, looksLikeJsonString } from '../../common/utils/jsonDetection';
import { evaluateFilterGroupsAgainstRow } from '../../common/utils/filterEvaluator';
import type { LooseRelationship } from '../../common/services/metaInfoApi';
import { SpatialCellRenderer } from '../../common/components/cells/SpatialCellRenderer';
import { safeToISOString } from '../../../../utils/dateUtils';
import { JsonCellRenderer } from '../../common/components/cells/JsonCellRenderer';
import { CELL_EXPAND_WRAPPER_CLASS } from '../../common/components/cells/cellExpandButton';
import { JsonLooseFkBadges } from '../../common/components/filters/JsonLooseFkBadges';
import { withLookupLabel } from '../../common/components/cells/LookupLabelSuffix';
import type { LookupLabelInfo } from '../../common/hooks/useLookupLabelMaps';
import { findLooseRelationshipsForColumn } from '../../common/utils/looseFkResolver';
import { formatNumericDisplay, type NumericGroupingMode } from '../../common/utils/columnAlignment';
import {
  shouldExpandToDialog,
  shouldShowExpandIconForClass,
  detectContentMode,
  type ExpandedEditMode,
  type ExpandIconColumnClass,
} from '../utils/expandedEditDetection';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { ColumnForeignKeyInfo } from '../hooks/useTableMetadata';
import type { TableExplorerExpandedEditState } from './TableExplorerGrid';
import type { TableExplorerCellValueCodec, TableExplorerRelationshipPort } from './tableExplorerHostPorts';

/** Normalized column shape the Table Explorer grid derives from its props
 *  (mirrors the elements of TableExplorerGrid's `normalizedColumns` memo). */
export type TableExplorerRowColumnDef = {
  id: string;
  key: string;
  name: string;
  dataType: string;
  isPrimaryKey: boolean;
  isForeignKey: boolean;
  isIdentity: boolean;
  nullable: boolean;
  fkInfo?: ColumnForeignKeyInfo;
  jsonClassification?: unknown;
  isNativeEnumType?: boolean;
};

const MAX_ARRAY_FK_PILLS = 5;


type SpatialRendererArgs = Parameters<typeof SpatialCellRenderer>[0];
type EditableCellSmartContext = React.ComponentProps<typeof EditableCell>['smartContext'];

/**
 * Everything a Table Explorer data row needs from the grid, bundled in ONE
 * memoized object so the row component can bail out of re-renders while
 * scrolling. Identity contract mirrors ResultsGrid's `ResultsGridRowCtx`: the
 * grid rebuilds this only when something that affects row rendering changes
 * (column sizing/pinning, selection, pending edits, metadata) — never on a
 * pure scroll. All function members are the grid's own `useCallback`s, whose
 * dep lists provide exactly that invalidation.
 */
export type TableExplorerRowCtx = {
  columnDefById: Map<string, TableExplorerRowColumnDef>;
  /** Per-column expand-icon classification (see `classifyExpandIconColumn`). */
  expandIconClassById: Map<string, ExpandIconColumnClass>;
  numericColumnIds: Set<string>;
  groupedNumericColumnIds: Set<string>;
  /** PP-01 — how grouped columns render; read once by the grid, never per cell. */
  numericGrouping: NumericGroupingMode;
  minColumnWidth: number;
  maxAutoColumnWidth: number;
  rowHeight: number;
  isEditMode: boolean;
  showRowDetailsExpander: boolean;
  /**
   * Host seam (S1 Task 1.17): render ∅ for a key the row does NOT carry,
   * distinct from an explicit NULL. `false` (every SQL caller) keeps today's
   * single NULL rendering.
   */
  absentValueGlyph: boolean;
  /** Host seam: an EXTRA editability veto on top of the identity / PK rules. */
  isHostCellEditable: (columnId: string, row: Record<string, unknown>) => boolean;
  cellValueCodec?: TableExplorerCellValueCodec;
  relationshipPort?: TableExplorerRelationshipPort;
  /** Host seam: a note on a pending inserted row (S4 review H, H1); SQL passes none. */
  insertedRowNote?: (tempId: string) => string | undefined;
  /** Host seam (C2 N11): presentation-only text for a plain cell; the title / copy / export keep the raw value. */
  displayText?: (value: unknown, columnName: string, row: Record<string, unknown>) => string | undefined;
  connectionId?: string;
  database?: string;
  engine?: DatabaseEngineId;
  sourceSchemaForTable?: string;
  sourceTableForTable?: string;
  lobColumns?: string[];
  onFetchFullCellValue?: (columnName: string, primaryKey: Record<string, unknown>) => Promise<unknown>;
  nativeSpatialColumnIds: SpatialRendererArgs['nativeSpatialColumnIds'];
  declaredSpatialColumnIds: SpatialRendererArgs['declaredSpatialColumnIds'];
  declaredSpatialColumns: SpatialRendererArgs['declaredSpatialColumns'];
  forcedJsonColumnIds: Set<string>;
  declaredJsonColumnIds: Set<string>;
  jsonColumnIdSet: Set<string>;
  looseRelationships: LooseRelationship[] | undefined;
  looseRelsByColumn: Map<string, LooseRelationship[]>;
  /** Lookup-enum key→label pairs per column id (`useLookupLabelMaps`) — the
   *  muted `· <label>` suffix an FK cell renders after its link. Presentation
   *  only; mirrored in the Query Editor's `useCellRenderer`. */
  lookupLabelInfos: Map<string, LookupLabelInfo>;
  /** Pinned columns' sticky placement: side + the CSS variable (set on the
   *  <table> by the grid) carrying the live offset, + whether this column is
   *  the boundary one (gets the pinned-edge border). Width/offset changes
   *  flow through CSS — rows never re-render for a resize drag. */
  pinnedStyleByColumnId: Map<string, { side: 'left' | 'right'; varName: string; isEdge: boolean }>;
  isCellSelected: (rowIndex: number, columnId: string) => boolean;
  onCellMouseDown: (rowIndex: number, columnId: string, event: React.MouseEvent) => void;
  onCellMouseEnter: (rowIndex: number, columnId: string) => void;
  /** Row-level pending state, resolved with ONE primary-key computation —
   *  cells read updates from the returned map instead of re-deriving the PK
   *  per cell (a measured edit-mode scroll hotspot). Null = nothing pending. */
  getRowPendingState: (row: Record<string, unknown>) => { deleted: boolean; updates: Map<string, unknown> } | null;
  getPrimaryKeyFromRow: (row: Record<string, unknown>) => Record<string, unknown>;
  onRowSelect?: (args: { rowIndex: number; row: Record<string, unknown> }) => void;
  onToggleRowDetails?: (args: { rowIndex: number; row: Record<string, unknown> }) => void;
  handleStartQuickEdit: (rowIndex: number, columnId: string, originalValue: unknown, row: Record<string, unknown>) => void;
  handleQuickEditConfirm: (newValue: unknown) => void;
  handleQuickEditCancel: () => void;
  handleStartFullEdit: (rowIndex: number, columnId: string) => void;
  handleFullEditConfirm: (newValue: unknown, rowData: Record<string, unknown>, columnName: string) => void;
  handleFullEditCancel: () => void;
  handleStartExpandedEdit: (
    rowIndex: number,
    columnId: string,
    value: unknown,
    mode: ExpandedEditMode,
    row: Record<string, unknown>,
    options?: { readOnly?: boolean; canEdit?: boolean },
  ) => void;
  handleDeleteRow: (row: Record<string, unknown>) => void;
  handleUndoDeleteRow: (row: Record<string, unknown>) => void;
  handleRemoveInsertRow: (tempId: string) => void;
  setExpandedEditState: React.Dispatch<React.SetStateAction<TableExplorerExpandedEditState | null>>;
  buildEditSmartContext: (columnName: string, rowData: Record<string, unknown>) => EditableCellSmartContext;
};

type TableExplorerGridRowProps = {
  row: Row<Record<string, unknown>>;
  actualRowIndex: number;
  isSelectedRow: boolean;
  isRowDetailsExpanded: boolean;
  /** Column being quick-edited in THIS row, else null. */
  quickEditColumnId: string | null;
  /** Column being full-edited in THIS row (edit mode only), else null. */
  fullEditColumnId: string | null;
  /** Column whose (grid-level, delegated) context menu is open in THIS row, else null. */
  menuColumnId: string | null;
  /** Digest of THIS row's selected columns ("" = none) — the grid derives it
   *  from the selection Set so a drag re-renders only rows whose own
   *  membership changed (ctx reads selection through a stable ref). */
  selectionSignature: string;
  ctx: TableExplorerRowCtx;
};

/**
 * One virtualized data row of the Table Explorer grid.
 *
 * `React.memo` with the comparator below is the core of the grid's scroll
 * performance — see `ResultsGridDataRow` (Query Editor) for the full design
 * notes; this is its Table Explorer parity twin. The table's `data` is the
 * visible slice, so the TanStack `row` WRAPPER is recreated per scroll; the
 * comparator keys on `row.original` (stable per data row) + `row.id` instead.
 * The per-cell Radix context menu was delegated to ONE grid-level menu — data
 * cells carry `data-row-index`/`data-column-id` for it.
 */
function tableExplorerGridRowPropsEqual(prev: TableExplorerGridRowProps, next: TableExplorerGridRowProps): boolean {
  return (
    prev.row.original === next.row.original &&
    prev.row.id === next.row.id &&
    prev.actualRowIndex === next.actualRowIndex &&
    prev.isSelectedRow === next.isSelectedRow &&
    prev.isRowDetailsExpanded === next.isRowDetailsExpanded &&
    prev.quickEditColumnId === next.quickEditColumnId &&
    prev.fullEditColumnId === next.fullEditColumnId &&
    prev.menuColumnId === next.menuColumnId &&
    prev.selectionSignature === next.selectionSignature &&
    prev.ctx === next.ctx
  );
}

export const TableExplorerGridRow = React.memo(function TableExplorerGridRow({
  row,
  actualRowIndex,
  isSelectedRow,
  isRowDetailsExpanded,
  quickEditColumnId,
  fullEditColumnId,
  menuColumnId,
  ctx,
}: TableExplorerGridRowProps) {
  // `selectionSignature` is deliberately not destructured: cells read the
  // selection via ctx.isCellSelected (a stable ref-backed getter); the prop
  // exists purely so the memo comparator re-renders this row when its own
  // selection membership changes.
  const {
    columnDefById,
    numericColumnIds,
    groupedNumericColumnIds,
    numericGrouping,
    minColumnWidth,
    maxAutoColumnWidth,
    rowHeight,
    isEditMode,
    showRowDetailsExpander,
    connectionId,
    database,
    engine,
    sourceSchemaForTable,
    sourceTableForTable,
    lobColumns,
    onFetchFullCellValue,
  } = ctx;

  const rowData = row.original;
  const isInsertedRow = '_tempId' in rowData;
  const insertedNote = isInsertedRow ? ctx.insertedRowNote?.(rowData._tempId as string) : undefined;
  // ONE pending-state resolution for the whole row (one PK computation);
  // every cell below reads from it instead of calling getCellPendingValue.
  const rowPending = !isInsertedRow ? ctx.getRowPendingState(rowData) : null;
  const rowDeleted = rowPending?.deleted ?? false;
  // Zebra by ABSOLUTE row index so a row keeps its stripe while scrolling
  // (window-relative parity made stripes crawl under the data by one row per
  // row scrolled — and defeated row memoization).
  const zebraEven = actualRowIndex % 2 === 0;

  return (
    <tr
      className={`
        border-b border-border/50
        transition-colors
        ${showRowDetailsExpander && isRowDetailsExpanded ? 'outline outline-1 outline-primary/40' : ''}
        ${isSelectedRow ? 'ring-2 ring-primary/40 ring-inset' : ''}
        ${/* T6.1c (G-6.1) — the pending-row axis is the SAME
            add/change/remove axis Data Compare settled, so it takes
            the same member-by-member rule (see `DataCompareResults
            Tabs.tsx`): a member moves onto a token only where a
            token already carries that exact meaning ON THIS
            SURFACE. Pending-EDIT already consumes `--energy-soft`
            (see the cell classes below), so the neighbouring tokens
            are not hypothetical here. Delete -> `--destructive`,
            which means precisely "this removes data". Insert keeps
            its raw green: there is no green token, and inventing
            one is the same locked decision Added's green is
            exempted under — not a call to make in a sweep.
            Chosen by MEASUREMENT, not by eye. The alphas below
            reproduce the previous hardcoded wash almost exactly
            (painted pixels sampled in Chromium over --background):
              light  red-100    #ffe2e2 -> destructive/20 #fbe3e2
              dark   red-900/30 #311621 -> destructive/30 #321420
            i.e. rgb distance 4.1 and 2.4 — below the visible
            threshold, so this is pure hygiene with no restyle
            smuggled in. NOTE the two alphas differ ON PURPOSE:
            --destructive is a much darker red on dark (L 0.4 vs
            0.7), so matching the old wash needs more of it there.
            Parity checked (CLAUDE.md two-grid rule): `ResultsGrid`
            has no deleted/inserted row state at all — row delete
            and insert are Table Explorer affordances — so there is
            nothing to mirror. */ ''}
        ${rowDeleted
          ? 'bg-destructive/20 dark:bg-destructive/30 line-through opacity-60'
          : isInsertedRow
            ? `bg-green-50 dark:bg-green-900/20${insertedNote ? ' outline-1 outline-dashed -outline-offset-1 outline-amber-500' : ''}`
            : isSelectedRow
              ? 'bg-primary/20 hover:bg-primary/20'
            : zebraEven
              ? 'bg-background hover:bg-selection'
              : 'bg-muted/20 hover:bg-selection'
        }
      `}
      style={{ height: rowHeight }}
      onClick={() => ctx.onRowSelect?.({ rowIndex: actualRowIndex, row: rowData })}
      aria-selected={isSelectedRow}
      // U13: a stable hook for e2e coverage of the scroll-into-view +
      // focus behavior — inserted rows don't have a primary key to
      // build a more semantic test id from, so the temp id is it.
      data-testid={isInsertedRow ? `inserted-row-${rowData._tempId as string}` : undefined}
      title={insertedNote}
      data-inserted-note={insertedNote ? 'uncertain' : undefined}
    >
      {showRowDetailsExpander && (
        <td
          className="px-1 py-0 border-r border-grid-rule bg-muted/20"
          // See the matching header cell: `minWidth` keeps the greedy
          // filler from squeezing this gutter in auto layout.
          style={{ width: 34, minWidth: 34 }}
        >
          {!isInsertedRow && !rowDeleted && (
            <button
              type="button"
              data-testid={`row-details-toggle-${actualRowIndex}`}
              className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              onClick={(event) => {
                event.stopPropagation();
                ctx.onToggleRowDetails?.({ rowIndex: actualRowIndex, row: rowData });
              }}
              title="Open row details"
              aria-label="Open row details"
            >
              {isRowDetailsExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          )}
        </td>
      )}
      {row.getVisibleCells().map((cell) => {
        const pinInfo = ctx.pinnedStyleByColumnId.get(cell.column.id);
        const pinnedCellStyle = pinInfo ? {
          position: 'sticky' as const,
          [pinInfo.side]: `var(${pinInfo.varName})`,
          zIndex: 20,
          background: zebraEven ? 'var(--background)' : 'color-mix(in srgb, var(--muted) 20%, var(--background))',
          ...(pinInfo.isEdge ? pinnedColumnBorderStyle(pinInfo.side) : {}),
        } : {};
        const cellValue = cell.getValue();
        const columnName = cell.column.id;
        const columnDef = columnDefById.get(columnName);
        const isPrimaryKeyColumn = columnDef?.isPrimaryKey || false;
        const isIdentityColumn = columnDef?.isIdentity || false;

        // Determine if this cell is editable:
        // - Identity columns are NEVER editable (auto-generated)
        // - PK columns are editable for inserted rows, read-only for existing rows
        const isCellEditable =
          !isIdentityColumn
          && (!isPrimaryKeyColumn || isInsertedRow)
          && ctx.isHostCellEditable(columnName, rowData);

        // Check if this cell is in quick edit mode
        const isQuickEditing = quickEditColumnId === columnName;

        // Check for pending change on this cell (in full edit mode) — read
        // from the row-level pending state resolved once above.
        const pendingValue = rowPending?.updates.has(columnName)
          ? { hasPending: true as const, value: rowPending.updates.get(columnName) }
          : { hasPending: false as const };

        const displayValue = (() => {
          // Show pending value if available
          const valueToDisplay = pendingValue.hasPending ? pendingValue.value : cellValue;
          if (valueToDisplay === null || valueToDisplay === undefined) return 'NULL';
          if (typeof valueToDisplay === 'object') return JSON.stringify(valueToDisplay);
          return String(valueToDisplay);
        })();

        // Check if this cell is in full edit mode. A host-vetoed cell never
        // renders an editor, whatever set the editing cell (S4 review C, N2;
        // no host = SQL = always allowed, unchanged).
        const isFullEditing = isEditMode && fullEditColumnId === columnName && ctx.isHostCellEditable(columnName, rowData);

        // Quick edit cell rendering
        if (isQuickEditing && columnDef) {
          return (
            <td
              key={cell.id}
              className="px-1 py-0.5 border-r border-grid-rule last:border-r-0 bg-blue-50 dark:bg-blue-900/20"
              style={{
                minWidth: minColumnWidth,
                maxWidth: maxAutoColumnWidth,
                ...pinnedCellStyle,
              }}
            >
              <EditableCell
                value={cellValue}
                valueCodec={ctx.cellValueCodec}
                valueTag={ctx.cellValueCodec?.tagForCell?.(rowData, columnName)}
                column={{
                  name: columnDef.name,
                  dataType: columnDef.dataType,
                  nullable: columnDef.nullable ?? true,
                  isPrimaryKey: columnDef.isPrimaryKey,
                  isForeignKey: columnDef.isForeignKey,
                }}
                isEditing={true}
                isPending={false}
                onStartEdit={() => {}}
                onConfirm={ctx.handleQuickEditConfirm}
                onCancel={ctx.handleQuickEditCancel}
              />
            </td>
          );
        }

        // Full edit mode cell rendering (when cell is being edited)
        if (isFullEditing && columnDef) {
          // Get the current value (from insert row or pending change or original)
          const currentValue = isInsertedRow
            ? rowData[columnName]
            : pendingValue.hasPending
              ? pendingValue.value
              : cellValue;

          return (
            <td
              key={cell.id}
              className="px-1 py-0.5 border-r border-grid-rule last:border-r-0 bg-blue-50 dark:bg-blue-900/20"
              style={{
                minWidth: minColumnWidth,
                maxWidth: maxAutoColumnWidth,
                ...pinnedCellStyle,
              }}
            >
              <EditableCell
                value={currentValue}
                valueCodec={ctx.cellValueCodec}
                valueTag={ctx.cellValueCodec?.tagForCell?.(rowData, columnName)}
                column={{
                  name: columnDef.name,
                  dataType: columnDef.dataType,
                  nullable: columnDef.nullable ?? true,
                  isPrimaryKey: columnDef.isPrimaryKey,
                  isForeignKey: columnDef.isForeignKey,
                }}
                isEditing={true}
                isPending={false}
                onStartEdit={() => {}}
                onConfirm={(newValue) => ctx.handleFullEditConfirm(newValue, rowData, columnName)}
                onCancel={ctx.handleFullEditCancel}
                smartContext={ctx.buildEditSmartContext(columnName, rowData)}
              />
            </td>
          );
        }

        // Value + expand-affordance inputs, hoisted so the <td> can carry the
        // `data-expand` marker the grid-level FLOATING expand button reads.
        // The affordance is delegated (see TableExplorerGrid): one overlay
        // button for the whole grid instead of a button + icon mounted in
        // every expandable cell — on unsized-text tables (PG `text`, SQLite
        // `TEXT`, LOBs) that per-cell DOM measured ~2x slower window mounts.
        const valueToShow = pendingValue.hasPending ? pendingValue.value : cellValue;
        const isLobColumn = lobColumns?.includes(columnName) ?? false;
        const showExpand = isLobColumn
          || shouldShowExpandIconForClass(ctx.expandIconClassById.get(columnName) ?? 'content-based', valueToShow);
        /** Which delegated expand glyph this cell advertises (`td[data-expand]`); set by renderCellValue. */
        let expandAffordance: 'text' | 'json' | null = null;

        // Helper to render cell value (handles FK popovers and formatting)
        const renderCellValue = () => {
          // Host seam (S1 Task 1.17): a key the row does not carry at all.
          // Only an ABSENT own property renders ∅ — an explicit null is still
          // NULL, and `0` / `false` / `''` stay values. A pending edit that
          // REMOVES the property (an `undefined` pending value — only an
          // absent-glyph host produces one, S4 review B, R5) is absent too.
          if (
            ctx.absentValueGlyph
            && (pendingValue.hasPending
              ? pendingValue.value === undefined
              : !Object.prototype.hasOwnProperty.call(rowData, columnName))
          ) {
            return <span className="text-muted-foreground/60 text-xs" title="No such property">∅</span>;
          }

          // NULL/undefined values
          if (valueToShow === null || valueToShow === undefined) {
            const nullContent = <span className="text-muted-foreground/60 italic text-xs">NULL</span>;
            // The expand affordance survives for NULL when the column type
            // supports it; the wrapper keeps the reserved gutter so the
            // delegated button never covers the content.
            if (showExpand && columnDef) {
              expandAffordance = 'text';
              return <span className={CELL_EXPAND_WRAPPER_CLASS}>{nullContent}</span>;
            }
            return nullContent;
          }

          // Array loose-FK: each element is a clickable lookup. Read-only
          // navigation — array cells aren't inline-editable in this slice,
          // so this branch fires regardless of edit mode.
          if (
            Array.isArray(valueToShow) &&
            columnDef?.fkInfo?.isLoose &&
            sourceSchemaForTable &&
            sourceTableForTable &&
            connectionId &&
            database
          ) {
            return (
              <ArrayFkCell
                elements={valueToShow as unknown[]}
                visibleCount={MAX_ARRAY_FK_PILLS}
                connectionId={connectionId}
                database={database}
                sourceSchema={sourceSchemaForTable}
                sourceTable={sourceTableForTable}
                sourceColumn={columnDef.name}
                surface="table_explorer"
                sourceRow={rowData}
                onOpenExpandedView={() =>
                  ctx.handleStartExpandedEdit(actualRowIndex, columnName, valueToShow, 'json', rowData)
                }
              />
            );
          }

          // Spatial cell: sniffer-derived summary + peek glyph. Placed
          // IMMEDIATELY BEFORE the JSON branch so spatial wins over JSON
          // for the same column. Returns null for non-spatial columns /
          // unsniffable values so we fall through to the JSON/default
          // handling below.
          if (columnDef && engine) {
            // LOB spatial cells hand the peek/map a bounded full-value
            // fetch (by PK); a non-LOB value is decoded directly. The PK map
            // is built ONLY when the LOB gate passes (and lazily at
            // open-click time below): `getPrimaryKeyFromRow` normalizes every
            // row key, so calling it unconditionally per cell was a measured
            // fast-scroll hotspot (~74 keys × 74 cells per row).
            const needsLobFetch =
              (lobColumns?.includes(columnDef.id) ?? false) &&
              !!onFetchFullCellValue &&
              typeof valueToShow === 'string';
            const lobPk = needsLobFetch ? ctx.getPrimaryKeyFromRow(rowData) : null;
            const fetchFullValue =
              lobPk && onFetchFullCellValue && Object.keys(lobPk).length > 0
                ? () => onFetchFullCellValue(columnDef.id, lobPk)
                : undefined;
            const spatialCell = SpatialCellRenderer({
              value: valueToShow,
              columnId: columnDef.id,
              columnName: columnDef.name,
              rowData,
              engine,
              dataType: columnDef.dataType,
              nativeSpatialColumnIds: ctx.nativeSpatialColumnIds,
              declaredSpatialColumnIds: ctx.declaredSpatialColumnIds,
              declaredSpatialColumns: ctx.declaredSpatialColumns,
              fetchFullValue,
              connectionId,
              database,
              onOpenFull: (input) =>
                ctx.setExpandedEditState({
                  rowIndex: actualRowIndex,
                  columnId: columnDef.id,
                  value: valueToShow,
                  mode: 'text',
                  primaryKey: lobPk ?? ctx.getPrimaryKeyFromRow(rowData),
                  isInsertedRow: '_tempId' in rowData,
                  tempId: '_tempId' in rowData ? (rowData._tempId as string) : undefined,
                  readOnly: true,
                  canEdit: false,
                  spatial: {
                    input,
                    engine,
                    dataType: columnDef.dataType,
                    fetchFullValue,
                    connectionId,
                    database,
                  },
                }),
            });
            if (spatialCell) return spatialCell;
          }

          // Shared JSON cell renderer: handles native parsed-object jsonb,
          // valid JSON strings, and the declared-on-text "invalid JSON" glyph.
          // Returns null for non-JSON columns so we fall through to the
          // generic object/string handling below.
          if (columnDef && (typeof valueToShow === 'object' || typeof valueToShow === 'string')) {
            const jsonStr = typeof valueToShow === 'object'
              ? JSON.stringify(valueToShow)
              : String(valueToShow);
            const pathRels =
              connectionId &&
              database &&
              sourceSchemaForTable &&
              sourceTableForTable
                ? findLooseRelationshipsForColumn(
                    ctx.looseRelationships,
                    sourceSchemaForTable,
                    sourceTableForTable,
                    columnDef.name,
                  ).filter((r) => r.sourcePath)
                : [];
            const baseContent = typeof valueToShow === 'object'
              ? <span className="font-mono text-xs truncate min-w-0">{jsonStr}</span>
              : <span className="truncate min-w-0">{jsonStr}</span>;
            const helperContent =
              pathRels.length > 0 && connectionId && database && sourceSchemaForTable && sourceTableForTable ? (
                <span className="flex items-center gap-1 min-w-0">
                  {baseContent}
                  <JsonLooseFkBadges
                    connectionId={connectionId}
                    database={database}
                    value={valueToShow}
                    sourceSchema={sourceSchemaForTable}
                    sourceTable={sourceTableForTable}
                    sourceColumn={columnDef.name}
                    rels={pathRels}
                    surface="table_explorer"
                  />
                </span>
              ) : (
                baseContent
              );
            const jsonCell = JsonCellRenderer({
              value: valueToShow,
              columnId: columnDef.id,
              forcedJsonColumnIds: ctx.forcedJsonColumnIds,
              declaredJsonColumnIds: ctx.declaredJsonColumnIds,
              jsonColumnIdSet: ctx.jsonColumnIdSet,
              onExpand: () =>
                ctx.handleStartExpandedEdit(actualRowIndex, columnName, valueToShow, 'json', rowData),
              cellContent: helperContent,
            });
            if (jsonCell) return jsonCell;
          }

          // E1 value-shape fallback (two-grids parity with
          // useCellRenderer): a JSON-shaped string in a column with
          // explicit `not-json` metadata — surface a tooltip, no
          // View-JSON button / formatting. JSON-classified cells
          // were already handled and returned by JsonCellRenderer
          // above, so this only fires for explicit not-json columns.
          if (
            typeof valueToShow === 'string'
            && columnDef
            && isExplicitlyNotJsonColumn(columnDef.jsonClassification as Parameters<typeof isExplicitlyNotJsonColumn>[0])
            && looksLikeJsonString(valueToShow)
          ) {
            return <span title="Looks like JSON">{valueToShow}</span>;
          }

          // Object values (plain objects/arrays only — not Date, RegExp, etc.)
          if (typeof valueToShow === 'object') {
            const isPlainObjOrArray = Array.isArray(valueToShow)
              || Object.getPrototypeOf(valueToShow) === Object.prototype
              || Object.getPrototypeOf(valueToShow) === null;
            if (isPlainObjOrArray) {
              expandAffordance = 'json';
              return (
                <span className={CELL_EXPAND_WRAPPER_CLASS}>
                  <span className="font-mono text-xs truncate min-w-0">{JSON.stringify(valueToShow)}</span>
                </span>
              );
            }
            // Non-plain objects (Date, etc.) — display in ISO format
            return <span className="text-xs truncate min-w-0">{valueToShow instanceof Date ? safeToISOString(valueToShow) : String(valueToShow)}</span>;
          }

          const rawDisplayStr = String(valueToShow);
          // PP-01 display grouping — presentation only. The `<td>`
          // title attribute and every copy/export path keep the raw
          // value (`displayValue` above is computed from the row).
          const displayStr = ctx.displayText?.(valueToShow, columnName, rowData)
            ?? (groupedNumericColumnIds.has(columnName)
              ? formatNumericDisplay(valueToShow, numericGrouping) ?? rawDisplayStr
              : rawDisplayStr);

          // FK cell with popover for exploration (only if not pending - FK popovers use original value)
          const cellContent = !pendingValue.hasPending && columnDef?.fkInfo && connectionId && database
            ? (() => {
              const candidateRels = ctx.looseRelsByColumn.get(columnDef.name) ?? [];
              const matchingLooseRels = rowData
                ? candidateRels.filter((r) => evaluateFilterGroupsAgainstRow(rowData, r.sourceFilter))
                : candidateRels;
              // The lookup label rides AFTER the link, never inside it — the FK
              // cell's value, navigation and copy paths are untouched.
              return withLookupLabel(
                <ForeignKeyPopover
                  connectionId={connectionId}
                  database={database}
                  referencedSchema={columnDef.fkInfo.referencedSchema || ''}
                  referencedTable={columnDef.fkInfo.referencedTable}
                  referencedColumn={columnDef.fkInfo.referencedColumn}
                  value={valueToShow}
                  isLoose={columnDef.fkInfo.isLoose}
                  sourceSchema={sourceSchemaForTable}
                  sourceTable={sourceTableForTable}
                  sourceColumn={columnDef.name}
                  surface="table_explorer"
                  sourceRow={rowData}
                  matchingLooseRels={matchingLooseRels}
                  relationshipPort={ctx.relationshipPort}
                >
                  <span className="truncate min-w-0">{displayStr}</span>
                </ForeignKeyPopover>,
                ctx.lookupLabelInfos.get(columnDef.id),
                valueToShow,
                // Parity with the Query Editor cell renderer: the FK cell owns
                // popover state, so its wrapper stays put whether or not a
                // label has resolved.
                { stableWrapper: true },
              );
            })()
            : <span className="truncate min-w-0">{displayStr}</span>;

          // Show expand icon for expandable text columns. JSON
          // columns are handled above by JsonCellRenderer; this
          // branch only fires for non-JSON expandable surfaces
          // (LOB, large text, XML).
          if (showExpand && columnDef) {
            const isForcedJsonCol = ctx.forcedJsonColumnIds.has(columnDef.id);
            const valueLooksJson = typeof valueToShow === 'string'
              && looksLikeJsonString(valueToShow);
            expandAffordance = isForcedJsonCol || valueLooksJson ? 'json' : 'text';
            return <span className={CELL_EXPAND_WRAPPER_CLASS}>{cellContent}</span>;
          }

          return cellContent;
        };
        // Render BEFORE the <td> so `expandAffordance` is settled for its marker.
        const cellInner = renderCellValue();

        const isCellSelected = ctx.isCellSelected(actualRowIndex, columnName);
        // U11 (user-reported): meaning always owns the ring, selection always
        // owns the fill — see `selectionAffordance.ts`. A pending edit keeps its
        // orange `MEANING_OUTLINE_CLASS` ring whether or not the cell is
        // selected; selecting it only changes the FILL to the blue selection
        // wash, replacing the orange one.
        const cellFill = resolveCellFill(
          isCellSelected,
          pendingValue.hasPending,
        );
        const isHoverEditableCell = isEditMode && !rowDeleted && isCellEditable;

        return (
          <td
            key={cell.id}
            // `data-row-index` / `data-column-id` are read by the grid-level
            // delegated context menu to resolve which cell was right-clicked.
            data-row-index={actualRowIndex}
            data-column-id={columnName}
            data-expand={expandAffordance ?? undefined}
            // A state wash must SURVIVE the pointer. Tailwind emits every
            // `hover:` variant after the plain utilities regardless of the
            // order they appear in this string — measured in the built CSS:
            // `.bg-energy-soft` @112869 and `.bg-primary/35` @115886 vs
            // `.hover:bg-muted/60` @161556. So the unconditional
            // `hover:bg-muted/60` outranked the pending-edit wash and the
            // "work in flight" cue vanished at exactly the moment the user
            // was looking at it.
            //
            // Fix follows the row-level precedent above (`isSelectedRow ?
            // 'bg-primary/20 hover:bg-primary/20' : …`): PIN hover to the
            // resting value, and emit the generic hover wash only when no
            // state wash is present. `cursor-pointer` stays on every editable
            // cell so the interactivity cue never depends on cell state.
            //
            // The cell-selection pin is a GUARD, not a live fix: cell
            // selection is `disabled: isEditMode` (see `useCellSelection`
            // in the grid), so `bg-primary/35` and `hover:bg-muted/60` cannot
            // co-occur today. It keeps the invariant true if that ever
            // changes — and the hover rules keep the same relative order as
            // their plain counterparts (measured: `.hover:bg-energy-soft`
            // @160448 < `.hover:bg-primary/35` @162219, mirroring the plain
            // pair), so a both-states cell would resolve identically at rest
            // and on hover.
            //
            // U11 (user-reported) replaces W7.8's contested-fill rule: a
            // selected pending cell no longer keeps its orange FILL with a
            // blue ring layered on top — the fill goes blue (`cellFill ===
            // 'selection'`, same as an ordinary selected cell) and the
            // orange moves entirely to an UNCONDITIONAL ring
            // (`MEANING_OUTLINE_CLASS`, on whenever `pendingValue.hasPending`
            // regardless of selection). The two washes below stay mutually
            // exclusive by construction — `resolveCellFill` decides in JS —
            // so `bg-primary/35` and `bg-energy-soft` still can't collide via
            // stylesheet order the way an unconditional pair would. The
            // ROW-level selection above already works this way (its
            // `bg-primary/20` yields to the pending-delete and inserted-row
            // washes while `ring-2 ring-primary/40 ring-inset` carries the
            // selection) — this keeps the cell level in line with it.
            //
            // The `bg-accent/50` open-menu wash marks the one cell the
            // delegated grid-level context menu is open on (two-grid parity
            // with ResultsGrid's affordance).
            className={`px-3 py-1 border-r border-grid-rule last:border-r-0 group/cell
                       whitespace-nowrap text-foreground overflow-hidden text-ellipsis
                       ${menuColumnId === columnName ? 'bg-accent/50' : ''}
                       ${numericColumnIds.has(columnName) ? 'text-right' : ''}
                       ${pendingValue.hasPending ? MEANING_OUTLINE_CLASS : ''}
                       ${cellFill === 'meaning' ? 'bg-energy-soft hover:bg-energy-soft' : ''}
                       ${cellFill === 'selection' ? 'bg-primary/35 hover:bg-primary/35' : ''}
                       ${isHoverEditableCell ? 'cursor-pointer' : ''}
                       ${isHoverEditableCell && !pendingValue.hasPending && !isCellSelected ? 'hover:bg-muted/60' : ''}`}
            // Widths come from the grid's <colgroup> (fixed layout); the
            // min/max here only shape the initial auto layout.
            style={{
              minWidth: minColumnWidth,
              maxWidth: maxAutoColumnWidth,
              ...pinnedCellStyle,
            }}
            title={displayValue}
            onMouseDown={(e) => {
              ctx.onCellMouseDown(actualRowIndex, columnName, e);
            }}
            onMouseEnter={() => {
              ctx.onCellMouseEnter(actualRowIndex, columnName);
            }}
            onDoubleClick={() => {
              if (isEditMode && !rowDeleted && isCellEditable) {
                // Check if this cell should use expanded edit
                const expandLike = columnDef ? { dataType: columnDef.dataType } : undefined;
                const expandDetection = shouldExpandToDialog(cellValue, expandLike);

                if (expandDetection.shouldExpand) {
                  const mode = detectContentMode(cellValue, expandLike);
                  ctx.handleStartExpandedEdit(actualRowIndex, columnName, cellValue, mode, rowData);
                } else {
                  ctx.handleStartFullEdit(actualRowIndex, columnName);
                }
              }
            }}
          >
            {pendingValue.hasPending ? (
              <div className="flex items-center min-w-0">
                <div className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap flex-1">
                  {cellInner}
                </div>
                <span className="ml-0.5 text-energy-text text-[10px] flex-shrink-0 leading-none">*</span>
              </div>
            ) : (
              cellInner
            )}
          </td>
        );
      })}
      {/* Actions column in edit mode */}
      {isEditMode && (
        <td className="px-2 py-1 border-r border-grid-rule last:border-r-0 whitespace-nowrap">
          {isInsertedRow ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
              onClick={() => ctx.handleRemoveInsertRow(rowData._tempId as string)}
              title="Remove new row"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          ) : rowDeleted ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
              onClick={() => ctx.handleUndoDeleteRow(rowData)}
              title="Undo delete"
            >
              <Undo2 className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
              onClick={() => ctx.handleDeleteRow(rowData)}
              title="Delete row"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </td>
      )}
      {/* PP-05 filler — inert: no handlers, not a TanStack column,
          so cell selection and every copy/export path skip it.
          `width: 100%` matches the header filler so the column stays
          greedy in auto layout (see the header filler in the grid).
          `select-none` keeps it out of a *native* text selection:
          without it Chromium serialises the empty cell as a trailing
          tab, so drag-selecting a row and copying yielded
          `1\tAlice\t` instead of `1\tAlice` (measured). */}
      <td
        aria-hidden="true"
        data-grid-filler="true"
        className="p-0 border-0 select-none"
        style={{ width: '100%' }}
      />
    </tr>
  );
}, tableExplorerGridRowPropsEqual);
